import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Store, CheckCircle, Loader2 } from "lucide-react";
import { authService } from "../api/authService";
import { useNotification } from "../contexts/NotificationContext";

export default function StoreSwitcherModal({ onClose, onSwitch }) {
  const { showSuccess, showError } = useNotification();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const currentTenantId = localStorage.getItem("tenantId") || "";

  useEffect(() => {
    authService
      .myTenants()
      .then((res) => {
        setTenants(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        const msg = String(err.message || err);
        setError(msg);
        showError("Gagal memuat daftar toko");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (tenant) => {
    if (tenant.id === currentTenantId) {
      // Still trigger callback to ensure parent state is in sync
      onSwitch?.(tenant);
      onClose();
      return;
    }
    try {
      localStorage.setItem("tenantId", tenant.id || "");
      localStorage.setItem("tenantName", tenant.name || "");
      localStorage.setItem("tenantSlug", tenant.slug || "");
    } catch {}
    showSuccess(`Beralih ke ${tenant.name}`);
    onSwitch?.(tenant);
    onClose();
  };

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4 backdrop-blur-sm modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl modal-panel-enter flex flex-col max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">Pilih Toko</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Pilih toko yang ingin Anda kelola
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-full transition">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {loading && (
            <div className="flex items-center justify-center py-10 gap-2 text-gray-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
              Memuat daftar toko...
            </div>
          )}

          {!loading && error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          {!loading && !error && tenants.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">
              Belum ada toko terkait akun Anda.
            </p>
          )}

          <div className="space-y-2">
            {tenants.map((t) => {
              const isActive = t.id === currentTenantId;
              const normalizeText = (value) => {
                const text = String(value ?? "").trim();
                if (!text) return "";
                const lower = text.toLowerCase();
                if (lower === "null" || lower === "undefined") return "";
                return text;
              };
              const address = normalizeText(t.settings?.address);
              const slug = normalizeText(t.slug);
              // Backend bisa kirim logo_url di beberapa lokasi yang berbeda
              let logoUrl =
                t.logo_url ||
                t.logo ||
                t.tenant_logo_url ||
                t.settings?.logo_url ||
                t.tenant?.logo_url ||
                "";
              // Fallback: pakai cache dari Konfigurasi Toko untuk tenant aktif
              if (!logoUrl && isActive) {
                try {
                  logoUrl = localStorage.getItem("tenantLogoUrl") || "";
                } catch {}
              }
              return (
                <button
                  key={t.id}
                  onClick={() => handleSelect(t)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition text-left ${
                    isActive
                      ? "border-teal-500 bg-teal-50"
                      : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}>
                  {logoUrl ? (
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ${
                        isActive ? "ring-teal-300" : "ring-gray-200"
                      }`}>
                      <img
                        src={logoUrl}
                        alt={t.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback ke icon Store jika gambar gagal dimuat
                          e.currentTarget.style.display = "none";
                          if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.classList.add(
                              isActive ? "bg-teal-600" : "bg-gray-100",
                            );
                            const fallback = document.createElement("div");
                            fallback.className =
                              "w-full h-full flex items-center justify-center";
                            fallback.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="${isActive ? "text-white" : "text-gray-500"}"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>`;
                            e.currentTarget.parentElement.appendChild(fallback);
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isActive ? "bg-teal-600" : "bg-gray-100"
                      }`}>
                      <Store
                        className={`w-4 h-4 ${isActive ? "text-white" : "text-gray-500"}`}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-semibold truncate ${isActive ? "text-teal-700" : "text-gray-800"}`}>
                      {t.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {slug && address
                        ? `${slug} • ${address}`
                        : slug || address || "-"}
                    </p>
                  </div>
                  {isActive && (
                    <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  )}
                  {!isActive && (
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${
                        t.status === "ACTIVE" || !t.status
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-gray-100 text-gray-600 border-gray-200"
                      }`}>
                      {t.status || "ACTIVE"}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
